import { getAppBaseUrl } from './getAppBaseUrl';
import type { Result, AppError } from '../../error/types';
import { ok, err } from '../../error/functions';

/**
 * @group Functions
 * @summary <blockquote>Get the full URL for a specific path in the application using the Result pattern.</blockquote>
 * @param path - The path to append to the base URL (should start with /)
 * @returns Result containing the full URL including the path or an AppError if base URL determination fails
 * @example
 * ```typescript
 * const result = getAppUrl('/api/users');
 * result.match({
 *   ok: (url) => console.log('Full URL:', url),
 *   err: (error) => console.error('Failed to get URL:', error.errorMessage)
 * });
 * ```
 */
/**
 * @internal
 */
export function getAppUrl(path: string): Result<string, AppError> {
  const baseUrlResult = getAppBaseUrl();
  
  return baseUrlResult.match({
    ok: (baseUrl) => {
      // Ensure path starts with / and remove any trailing slashes
      const normalizedPath = path.startsWith('/') ? path : `/${path}`;
      return ok(`${baseUrl}${normalizedPath}`);
    },
    err: (error) => {
      return err(error);
    }
  });
} 